/// Readback functions added from KnxBridge
import { showErrToast, showToast } from "../components/Toasts";
import { firstChildWithTag } from "../components/XmlUtils";
import KnxBridge, { gatewayIp, TableSizes } from "../KnxBridge";
import ButtonChannel from "../models/ButtonChannel";
import Channel from "../models/Channel";
import { ChannelType } from "../models/ChannelType";
import Device from "../models/Device";
import GroupAddress from "../models/GroupAdress";
import { CodeSegment } from "../models/programming/CodeSegment";
import { AssociationListEntry } from "../models/programming/DeviceProgramming";
import Signal from "../models/Signal";
import SignalTypes from "../models/SignalTypes";
import { EP } from "../performance/PerfomanceItem";
import ProductStorageHandler from "../persistence/ProductStorageHandle";
import { itx } from "../translationItx/itx";
import { knxReadBack } from "./KnxReadback";

const MergedProcedure = "MergedProcedure"; // no itx
const ProductProcedure = "ProductProcedure"; // no itx
const DefaultProcedure = "DefaultProcedure"; // no itx

const rb = knxReadBack();

class ReadBackViaKnxBridgeClass {
  async readBackAddresses(
    deviceAddress: string,
    addressTableOffset: string,
    LoadProcedureStyle: string,
  ): Promise<string[]> {
    const addressTableFromMemory = await KnxBridge.readAddressTableFromDevice(
      deviceAddress,
      addressTableOffset,
      LoadProcedureStyle,
    );
    const addrTableLength = addressTableFromMemory.length;
    const isMerged = LoadProcedureStyle == MergedProcedure;
    const countBytes = isMerged ? 2 : 1;
    let addressCount = parseInt(addressTableFromMemory.substring(0, countBytes * 2), 16);
    let setOfAddresses: string[] = [];
    for (let i = countBytes * 2; i < addrTableLength; i += 4) {
      if (i == countBytes * 2 && LoadProcedureStyle != MergedProcedure) continue;
      const addr = KnxBridge.hexToGroupAddress(addressTableFromMemory.substring(i, i + 4));
      setOfAddresses.push(addr);
    }
    return setOfAddresses;
  }

  async readBackAssociations(
    deviceAddress: string,
    associationTableOffset: string,
    LoadProcedureStyle: string,
    setOfAddresses: string[],
  ): Promise<any> {
    const associationTableFromMemory = await KnxBridge.readAssociationTableFromDevice(
      deviceAddress,
      associationTableOffset,
      LoadProcedureStyle,
    );
    const assocTableLength = associationTableFromMemory.length;
    const isMerged = LoadProcedureStyle == MergedProcedure;
    const countBytes = isMerged ? 2 : 1;
    const tableEntrySize = isMerged ? 4 : 2;

    let associationsCount = parseInt(associationTableFromMemory.substring(0, countBytes * 2), 16);
    let setOfAssociations: any = [];
    for (let i = countBytes * 2; i < assocTableLength; i += 4 * countBytes) {
      const addrTableIndex = parseInt(associationTableFromMemory.substring(i, i + tableEntrySize), 16);
      const signalNo = parseInt(associationTableFromMemory.substring(i + tableEntrySize, i + tableEntrySize * 2), 16);
      setOfAssociations.push({
        addr: setOfAddresses[addrTableIndex - 1],
        cob: signalNo,
      });
    }
    return setOfAssociations;
  }

  async readbackComObjTable(
    device: Device,
    deviceAddress: string,
    groupObjTableOffset: string,
    setOfAssociations: any,
    setOfCobs: Set<number>,
    LoadProcedureStyle: string,
  ): Promise<AssociationListEntry[]> {
    const associationList: AssociationListEntry[] = [];
    let SegmentType = "0";

    const groupObjTable = await KnxBridge.readGroupObjTableFromDevice(
      deviceAddress,
      groupObjTableOffset,
      LoadProcedureStyle,
    );

    if (groupObjTable) {
      const comObjCount = parseInt(groupObjTable.substring(0, 2), 16);
      const commEnableBitmask: number = 0x04;
      const commDisableBitmask: number = ~commEnableBitmask;

      for (let cob = 0, cobStartAddr = 8; cobStartAddr < groupObjTable.length; cob++, cobStartAddr += 8) {
        const configOffset = cobStartAddr + 2;
        const configOctet = groupObjTable.substring(configOffset, configOffset + 2);
        const typeOctet = groupObjTable.substring(configOffset + 2, configOffset + 4);
        if (!setOfCobs.has(cob)) {
          continue; // inactive COB
        }

        const configBin = parseInt(configOctet, 16).toString(2).padStart(8, "0");
        const [
          UpdateFlag7,
          TransmitFlag6,
          SegmentSelectorTypeFlag5,
          WriteFlag4,
          ReadFlag3,
          CommunicationFlag2,
          TransmitPriority1,
          TransmitPriority0,
        ] = configBin.split("").map((bit) => parseInt(bit));

        const priorityBits = `${TransmitPriority1}${TransmitPriority0}`;
        const priority =
          Object.keys(KnxBridge.priorityIndex).find((key) => KnxBridge.priorityIndex[key] === priorityBits) || "Low";

        const objectSizeIndex = parseInt(typeOctet, 16);
        const objectSize = KnxBridge.typeOctetTable[objectSizeIndex] || "unknown";

        const associationsWithCob = setOfAssociations.filter((item: any) => item.cob == cob);

        const Enabled = "Enabled"; // no itx
        const Disabled = "Disabled"; // no itx

        associationsWithCob.forEach((item: any) => {
          const entry: AssociationListEntry = {
            cob: cob,
            addr: item.addr,
            a: {
              UpdateFlag: UpdateFlag7 ? Enabled : Disabled,
              TransmitFlag: TransmitFlag6 ? Enabled : Disabled,
              WriteFlag: WriteFlag4 ? Enabled : Disabled,
              ReadFlag: ReadFlag3 ? Enabled : Disabled,
              CommunicationFlag: item.addr ? Enabled : Disabled,
              Priority: priority,
              ObjectSize: objectSize,
            },
          };
          associationList.push(entry);
        });
      }
    }
    return associationList;
  }

  async readbackComObjTableRel(
    device: Device,
    deviceAddress: string,
    groupObjTableOffset: string,
    setOfAssociations: any,
    setOfCobs: Set<number>,
    LoadProcedureStyle: string,
  ): Promise<AssociationListEntry[]> {
    const associationList: AssociationListEntry[] = [];
    let SegmentType = "0";

    const groupObjTable = await KnxBridge.readGroupObjTableFromDevice(
      deviceAddress,
      groupObjTableOffset,
      LoadProcedureStyle,
    );

    if (groupObjTable) {
      const comObjCount = parseInt(groupObjTable.substring(0, 4), 16);
      const commEnableBitmask: number = 0x04;
      const commDisableBitmask: number = ~commEnableBitmask;

      for (let cob = 1, cobStartAddr = 4; cobStartAddr < groupObjTable.length; cob++, cobStartAddr += 4) {
        const configOffset = cobStartAddr;
        const configOctet = groupObjTable.substring(configOffset, configOffset + 2);
        const typeOctet = groupObjTable.substring(configOffset + 2, configOffset + 4);
        // let hexNumber: number = parseInt(configOctet, 16);
        if (configOctet === "00" && typeOctet === "00") {
          continue; // inactive COB
        }
        // setOfCobs.add(cob);

        const configBin = parseInt(configOctet, 16).toString(2).padStart(8, "0");
        const [
          UpdateFlag7,
          TransmitFlag6,
          SegmentSelectorTypeFlag5,
          WriteFlag4,
          ReadFlag3,
          CommunicationFlag2,
          TransmitPriority1,
          TransmitPriority0,
        ] = configBin.split("").map((bit) => parseInt(bit));

        const priorityBits = `${TransmitPriority1}${TransmitPriority0}`;
        const priority =
          Object.keys(KnxBridge.priorityIndex).find((key) => KnxBridge.priorityIndex[key] === priorityBits) || "Low";

        const objectSizeIndex = parseInt(typeOctet, 16);
        const objectSize = KnxBridge.typeOctetTable[objectSizeIndex] || "unknown";

        const associationsWithCob = setOfAssociations.filter((item: any) => item.cob == cob);

        const Enabled = "Enabled"; // no itx
        const Disabled = "Disabled"; // no itx

        associationsWithCob.forEach((item: any) => {
          const entry: AssociationListEntry = {
            cob: cob,
            addr: item.addr,
            a: {
              UpdateFlag: UpdateFlag7 ? Enabled : Disabled,
              TransmitFlag: TransmitFlag6 ? Enabled : Disabled,
              WriteFlag: WriteFlag4 ? Enabled : Disabled,
              ReadFlag: ReadFlag3 ? Enabled : Disabled,
              CommunicationFlag: item.addr ? Enabled : Disabled,
              Priority: priority,
              ObjectSize: objectSize,
            },
          };
          associationList.push(entry);
        });
      }
    }
    return associationList;
  }

  async readbackActualParams(
    device: Device,
    deviceAddress: string,
    paramSegmentAddr: string,
    paramSegmentSize: number,
    AP: any,
    LoadProcedureStyle: string,
  ) {
    let paramSegment: CodeSegment | undefined;
    let paramAddrHex = "?????"; // no itx
    let paramTableSize = 0;

    const Static = firstChildWithTag(AP, "Static"); // no itx
    const memorySegments = device.programming.getKnxMemorySegments();
    if (LoadProcedureStyle == MergedProcedure) {
      // LoadStateMachine = 4  stores Device Parameters
      paramSegment = memorySegments.find((item) => item?.LoadStateMachine == 4 || item.Id.split("-")[1] == "04");
      paramAddrHex = paramSegmentAddr;
      paramTableSize = paramSegmentSize;
    } else if (LoadProcedureStyle == ProductProcedure) {
      paramSegment = memorySegments.find((item) => item.Address.toString() == paramSegmentAddr.toString());
      if (paramSegment) {
        paramAddrHex = KnxBridge.prefixHexType(paramSegment.Id.split("-")[1]);
      }
    }

    if (paramSegment && paramSegment?.Data) {
      let actualData = paramSegment.Data; // Current configurations
      if (LoadProcedureStyle == ProductProcedure) paramTableSize = actualData.length;
      const paramTableInMem = await KnxBridge.readMemoryBlockFromDevice(
        deviceAddress,
        paramAddrHex,
        paramTableSize,
        true,
      );

      // memory Segments reversemap logic starts here
      let allParamList: { [key: string]: string } = {};
      let allmoduleParamList: { [key: string]: { [key: string]: string } } = {};
      let changedParamCount = 1; // (intial value to not break out of recursive while loop)
      const segmentId = paramSegment.Id;
      let segment = device.programming.getCodeSegment(segmentId);

      // Set segmentData to readback Table from device
      const paramTableBin = KnxBridge.hexToUint8Array(paramTableInMem);
      segment.Data = paramTableBin;
      const maxIterations = 10; // Temporary loop breaking condition

      let counter = 0;
      while (changedParamCount > 0 && counter < maxIterations) {
        changedParamCount = 0;
        // device.programming.initCodeSegments(Static);
        // device.programming.createDefaultMappings();
        // device.knxInstanceData.executeAllCalculationsOnce(); // Check if needed
        device.updateAllFromKnxMetadataImmediately();
        // device.programming.getKnxMemorySegments();
        device.programming.readBackMemorySegments(); // readback Comparision and Updation

        // Check which param values are changed compared to previous run, and update in dictionary
        if (device.knxInstanceData) {
          if (device.knxInstanceData.params) {
            for (let i in device.knxInstanceData.params) {
              const param = device.knxInstanceData.params[i];

              const paramInList = allParamList[i];
              if (paramInList != undefined && paramInList == param.value) {
                continue;
              } else {
                changedParamCount++;
                allParamList[i] = param.value;
              }
            }
          }

          if (device.knxInstanceData.modules) {
            for (let i in device.knxInstanceData.modules) {
              const module = device.knxInstanceData.modules[i];

              let moduleInList = allmoduleParamList[i];
              if (moduleInList == undefined) {
                allmoduleParamList[i] = {};
                moduleInList = allmoduleParamList[i];
              }

              for (let j in module.params) {
                const param = module.params[j];
                const paramInList = moduleInList[j];
                if (paramInList != undefined && paramInList == param.value) {
                  continue;
                } else {
                  changedParamCount++;
                  allmoduleParamList[i][j] = param.value;
                }
              }
            }
          }

          counter++;
          console.log(`Iteration ${counter}: `);
          console.log(`Total Params Parsed : ${allParamList.length}`);
          console.log(`ChangedParamCount this Iteration: ${changedParamCount}`);

          // device.knxInstanceData.executeAllCalculationsOnce();
        }
      }

      console.log(`Readback of Parameter Table completed.`);
    }
  }

  async readbackDeviceMemory(device: Device): Promise<boolean> {
    function createOrGetGroupaddress(sFullAddress: string) {
      const sAddresses = sFullAddress.split("/");

      let level1 = state.addresses.findFirst((ga) => ga.address == sAddresses[0]);

      if (!level1) {
        level1 = new GroupAddress({}, state.addresses);
        state.addresses.addWithoutUpdate(level1);
        level1.address = sAddresses[0];
        level1.level = 1;
        level1.name = sAddresses[0];
        level1.isNew = true;
        level1.updateFullAddress();
      }

      let level2 = level1.children.findFirst((ga) => ga.address == sAddresses[1]);

      if (!level2) {
        level2 = new GroupAddress({}, level1);
        level1.children.addWithoutUpdate(level2);
        level2.address = sAddresses[1];
        level2.level = 2;
        level2.name = sAddresses[1];
        level2.isNew = true;
        level2.updateFullAddress();
      }

      let level3 = level2.children.findFirst((ga) => ga.address == sAddresses[2]);

      if (!level3) {
        level3 = new GroupAddress({}, level2);
        level2.children.addWithoutUpdate(level3);
        level3.address = sAddresses[2];
        level3.level = 3;
        level3.name = sAddresses[2];
        level3.isNew = true;
        level3.updateFullAddress();
      }

      return level3;
    }

    try {
      await EP(
        "knx.readbackDeviceMemory", // no itx
      ).runAsync(async () => {
        KnxBridge.percentUI = 0;
        KnxBridge.operationInProgress = true;

        KnxBridge.toastTextUI = rb.toastTitle.replace(
          itx("knxBridge.creating", "Creating"),
          itx("knxBridge.readback", "Reading"),
        ); // itx("knxBridge.readBackDeviceApplication", "ReadBack Device Application");
        KnxBridge.showProgressToast(KnxBridge.toastTextUI, 0);

        const deviceAddress = device.fullPhysicalAddress;
        const productData = device.template?.knxProductData;

        // device.name = deviceAddress + " - " + device.name;

        await device.updateAllFromKnxMetadataImmediately();
        const AP = device.getApplicationProgram();
        if (productData && AP) {
          const AP = device.getApplicationProgram();

          const LoadProcedureStyle = AP.a?.LoadProcedureStyle;

          const memorySegments = device.programming.getKnxMemorySegments();
          KnxBridge.increasePercent();

          // 1 Initial Read Operations
          const manIdFromMem = await KnxBridge.getManIdFromDevice(deviceAddress); // PID 12
          const apduLengthFromMem = await KnxBridge.getMaxAPDULengthFromDevice(deviceAddress, manIdFromMem); // PID 56 // !IMPORTANT!
          KnxBridge.increasePercent();

          // 2 VerifyCompatibleMaskVersionTask (PID 83)
          // Some devices like BuschJeager have no mask version as an accesible property
          // So for such devices we assume by default that it is valid.
          let validMaskVersion = false;
          let maskVersionFromMem = productData.productInfo.MaskVersion
            ? productData.productInfo.MaskVersion.split("-")[1]
            : "";
          validMaskVersion = maskVersionFromMem
            ? KnxBridge.BimM112Masks.some((mask) => mask.toLowerCase() == maskVersionFromMem.toLowerCase()) ||
              KnxBridge.SystemBMasks.some((mask) => mask.toLowerCase() == maskVersionFromMem.toLowerCase())
            : false;

          // 3 VerifyCompatibleManufacturerTask (PID 12)
          const manIdFromAP = productData.productInfo.ManufacturerId.split("-")[1];
          const manIdAltFromAP = productData.productInfo.OriginalManufacturer
            ? productData.productInfo.OriginalManufacturer.split("-")[1]
            : "";
          const validManufacturer = manIdFromAP == manIdFromMem || manIdAltFromAP == manIdFromMem;

          // 4 VerifyAdditionalResourcesTask
          let additionalResourceCheck = false;
          if (LoadProcedureStyle == ProductProcedure) additionalResourceCheck = true;
          else if (LoadProcedureStyle == MergedProcedure) {
            const versionFromMem =
              LoadProcedureStyle == MergedProcedure ? await KnxBridge.getVersionFromDevice(deviceAddress) : ""; // PID 25 Version
            const orderInfoFromMem =
              LoadProcedureStyle == MergedProcedure ? await KnxBridge.getOrderInfoFromDevice(deviceAddress) : ""; // PID 15 OrderInfo
            KnxBridge.increasePercent();
            additionalResourceCheck = versionFromMem && orderInfoFromMem;
          }

          // 6 BasicLoadProcedureTask  (Check if LoadProcedures exists in AP)
          const LoadProcedures = device.programming.getLoadProcedures();
          KnxBridge.increasePercent();

          const deviceObjects =
            Object.keys(device.deviceObjectTypes).length > 0
              ? device.deviceObjectTypes
              : await KnxBridge.getDeviceObjectTypes(device);
          const tableOffsets = await KnxBridge.getMemTableOffsetsFromDevice(
            deviceAddress,
            AP,
            LoadProcedureStyle,
            deviceObjects,
          );
          const appIdFromAP = ProductStorageHandler.getApplicationIdFromCatalogId(productData.productInfo.CatalogId);
          KnxBridge.increasePercent();

          const verifyStatus = validMaskVersion && validManufacturer && appIdFromAP && LoadProcedures;

          if (verifyStatus && additionalResourceCheck) {
            const deviceAddress = device.fullPhysicalAddress; //E.g. "1.1.2"
            const setOfCobs = new Set<number>();

            // Readback Address Table
            const setOfAddresses: string[] = await this.readBackAddresses(
              deviceAddress,
              tableOffsets.address,
              LoadProcedureStyle,
            );

            KnxBridge.increasePercent();

            // Readback Association Table
            const setOfAssociations: any = await this.readBackAssociations(
              deviceAddress,
              tableOffsets.association,
              LoadProcedureStyle,
              setOfAddresses,
            );

            setOfAssociations.forEach((assoc: any) => {
              setOfCobs.add(assoc.cob);
            });

            KnxBridge.increasePercent();

            let associationList: AssociationListEntry[] = [];

            // Readback ComObj Table
            if (LoadProcedureStyle == MergedProcedure) {
              associationList = await this.readbackComObjTableRel(
                device,
                deviceAddress,
                tableOffsets.groupObj,
                setOfAssociations,
                setOfCobs,
                LoadProcedureStyle,
              );
            } else {
              associationList = await this.readbackComObjTable(
                device,
                deviceAddress,
                tableOffsets.groupObj,
                setOfAssociations,
                setOfCobs,
                LoadProcedureStyle,
              );
            }

            const defaultProcedure = await KnxBridge.getDefaultProcedureFromMasterXml(maskVersionFromMem); // LP from master xml (Type: DefaultProcedure)
            let finalProcedure = defaultProcedure;
            if (LoadProcedureStyle == ProductProcedure) {
              finalProcedure = LoadProcedures;
              for (let i in finalProcedure.items) {
                const LoadProcedure = finalProcedure.items[i];
                if (LoadProcedure) {
                  let procedureIndex = 0;
                  for (procedureIndex; procedureIndex < LoadProcedure.items.length; procedureIndex++) {
                    const procedure = LoadProcedure.items[procedureIndex];
                    const tag = procedure["#name"];

                    if (tag == "LdCtrlAbsSegment") {
                      const LsmIdx = procedure.a.LsmIdx; // Load State Machine Id
                      if (LsmIdx == "3") {
                        const groupObjTableAddress = parseInt(tableOffsets.groupObj, 16);
                        const MemType = procedure.a.MemType;
                        if (MemType == "3") {
                          const Address = parseInt(procedure.a.Address);
                          if (Address > groupObjTableAddress) {
                            // Readback Param Table
                            await this.readbackActualParams(
                              device,
                              deviceAddress,
                              Address.toString(),
                              0,
                              AP,
                              LoadProcedureStyle,
                            );
                          }
                        }
                      }
                    }
                  }
                }
              }
            } else if (LoadProcedureStyle == MergedProcedure) {
              const tableSizes: TableSizes = await KnxBridge.getTableSizeList(
                deviceAddress,
                tableOffsets,
                memorySegments,
                AP,
                LoadProcedureStyle,
                new Set(setOfAddresses),
                associationList,
              ); // Get existing table sizes from memory
              // const paramTableInMemory = await this.readMemoryBlockFromDevice(
              //   deviceAddress,
              //   tableOffsets.param,
              //   tableSizes.param,
              //   true,
              // );
              await this.readbackActualParams(
                device,
                deviceAddress,
                tableOffsets.param,
                tableSizes.param,
                AP,
                LoadProcedureStyle,
              );
            }

            KnxBridge.increasePercent();

            // assign back signals /addresses in Device

            await device.updateAllFromKnxMetadataImmediately();
            const knxProductData = device.template?.knxProductData;
            if (device.getApplicationProgram()) {
              associationList.forEach((assoc) => {
                let signal = device.allSignals().find((s) => parseInt(s.number) === assoc.cob);

                if (!signal) {
                  //
                  // no signal swith number "assoc.cob" found so far in the device
                  // so we create one with tag "@I" to not loos informations
                  //

                  let channel = device.channels.getItemByTag("@I"); // no itx
                  if (!channel) {
                    //
                    // @I signals are always hold in channel @I
                    //
                    if (device.isActor) {
                      channel = new Channel({ tag: "@I" }, channel); // no itx
                    } else {
                      channel = new ButtonChannel({ tag: "@I" }, channel); // no itx
                    }

                    channel.isRequestChannel = true;
                    channel.channelType = ChannelType.Switch;
                    channel.name = "Imported";

                    device.channels.add(channel);
                  }

                  signal = new Signal({ number: assoc.cob.toString(), name: "imported" }, channel);
                  signal.type = SignalTypes.Unknown;
                  channel.signals.add(signal);

                  console.log(`Signal with number ${assoc.cob} not found, created it`);
                  console.log("all @i signals:", channel.signals.asArray());
                }

                signal.a = { ...signal.a, ...assoc.a };

                if (assoc.addr) {
                  // Add GroupAddress to signal

                  let newGroupAddress = createOrGetGroupaddress(assoc.addr);
                  signal.addGroupAddress(newGroupAddress);
                }
              });
            }
          } else {
            console.error("PreLoadProcedure Error: device PID(s) mismatch with AppProgram properties");
          }
          showToast({ text: itx("knxBridge.readbackComplete", "Readback Complete.") });
        } else {
          showErrToast(
            itx("ReedBackViaKnxBridge.applivationProgramNotLoaded", "Application Program not loaded"),
            itx("ReedBackViaKnxBridge.readbackFailed", "Readback Failed"),
          );
        }
      });
    } catch (error) {
      if (device.fullPhysicalAddress == KnxBridge.lastDeviceUnreachable) {
        showErrToast(itx("ReadBackViaKnxBridge.deviceUnreachable", "Device Unreachable"), "Readback Failed");
      } else if (!gatewayIp) {
        showErrToast(
          itx("ReadBackViaKnxBridge.knxLinkDisconnected", "KNX link disconnected during verification Operation"),
          "Readback Failed",
        );
      } else {
        showErrToast("An unexpected error occured during verification", "Readback Failed");
        KnxBridge.operationFailure = true;
      }
    } finally {
      KnxBridge.operationInProgress = false;
      KnxBridge.updateTotalMemoryDiff();
    }
    return true;
  }
}

const ReadBackViaKnxBridge = new ReadBackViaKnxBridgeClass();
export default ReadBackViaKnxBridge;
